import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { hreflangLinks, localizedPath, isFrPath, enTwinFor, SITE_BASE_URL } from "@/i18n/paths";
import { useLocation } from "react-router-dom";
import { MaskedReveal, FadeIn } from "@/components/Reveal";
import { EditorialMarquee } from "@/components/EditorialMarquee";
import { JsonLd, organizationSchema } from "@/components/JsonLd";
import { services } from "@/data/services";
import { cities } from "@/data/cities";
import { caseStudies } from "@/data/caseStudies";
import { TESTIDS } from "@/constants/testIds";

// Live clock component (Quebec / Eastern time)
const LiveClock = () => {
  const [now, setNow] = React.useState(new Date());
  React.useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const t = now.toLocaleTimeString("en-CA", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone: "America/Montreal",
  });
  return (
    <div
      data-testid={TESTIDS.hero.clock}
      className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400"
    >
      <div className="text-amber-500">◉ Live</div>
      <div className="mt-1 text-white">Montréal · {t}</div>
      <div className="mt-1">45°30′N · 73°33′W</div>
    </div>
  );
};

const Hero = () => {
  const { t } = useLang();
  const ref = React.useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);

  return (
    <section
      ref={ref}
      data-testid={TESTIDS.hero.root}
      className="relative min-h-[100vh] w-full overflow-hidden bg-black"
    >
      {/* Background image with parallax */}
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1517913451214-e22ce660e086?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400"
          alt="Quebec security patrol at night"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(0,0,0,0.6)_80%)]" />
      </motion.div>

      {/* HUD overlays */}
      <div className="absolute top-[96px] left-6 md:left-10 z-10">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
        >
          <LiveClock />
        </motion.div>
      </div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute top-[96px] right-6 md:right-10 z-10 text-right font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400"
      >
        <div className="text-amber-500">◉ On Duty</div>
        <div className="mt-1 text-white">1,240+ Officers Active</div>
        <div className="mt-1">17 Regions · 400+ Sites</div>
      </motion.div>

      {/* Main title */}
      <motion.div style={{ opacity }} className="relative z-10 h-full min-h-[100vh] flex flex-col justify-end px-6 md:px-10 pb-16 md:pb-24 max-w-[1600px] mx-auto">
        <div
          data-testid={TESTIDS.hero.kicker}
          className="overflow-hidden mb-8"
        >
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 flex items-center gap-3"
          >
            <span className="w-8 h-px bg-amber-500" />
            {t("home.hero_kicker")}
          </motion.div>
        </div>

        <h1
          data-testid={TESTIDS.hero.title}
          className="font-display uppercase text-white font-black leading-tight tracking-[-0.02em]"
        >
          <MaskedReveal delay={0.4}>
            <span className="block text-base md:text-lg font-bold tracking-[0.08em] text-zinc-200 normal-case mb-4">
              {t("home.h1")}
            </span>
          </MaskedReveal>
        </h1>
        <div
          aria-hidden="true"
          className="font-display uppercase text-white font-black leading-tight tracking-[-0.02em] text-[28px]"
        >
          <MaskedReveal delay={0.5}>
            <span>{t("home.hero_line1")}</span>
            <span className="text-amber-500">{t("home.hero_line2")}</span>
            <span>{t("home.hero_line3")}</span>
          </MaskedReveal>
        </div>

        <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-end">
          <FadeIn delay={1.4} className="md:col-span-6">
            <p
              data-testid={TESTIDS.hero.sub}
              className="text-base md:text-lg text-zinc-300 max-w-xl leading-relaxed"
            >
              {t("home.hero_sub")}
            </p>
          </FadeIn>
          <FadeIn delay={1.6} className="md:col-span-6 flex flex-col sm:flex-row gap-3 md:justify-end">
            <Link
              to="/contact"
              data-testid={TESTIDS.hero.primaryCta}
              className="group inline-flex items-center justify-between gap-4 bg-amber-500 hover:bg-amber-400 text-black h-14 px-6 text-[11px] font-mono uppercase tracking-[0.2em] transition-colors"
            >
              {t("common.request_quote")}
              <ArrowUpRight className="w-5 h-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <Link
              to="/services"
              data-testid={TESTIDS.hero.secondaryCta}
              className="group inline-flex items-center justify-between gap-4 border border-white/20 hover:border-amber-500 text-white h-14 px-6 text-[11px] font-mono uppercase tracking-[0.2em] transition-colors"
            >
              {t("common.all_services")}
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </FadeIn>
        </div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2 }}
        className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500"
      >
        ↓ scroll
      </motion.div>
    </section>
  );
};

const StatBar = () => {
  const { lang } = useLang();
  const stats = lang === "fr"
    ? [
        { k: "1,240+", v: "Agents" },
        { k: "17", v: "Régions" },
        { k: "400+", v: "Sites actifs" },
        { k: "12min", v: "Réponse alarme" },
        { k: "24/7", v: "Centre de commandement" },
      ]
    : [
        { k: "1,240+", v: "Officers" },
        { k: "17", v: "Regions" },
        { k: "400+", v: "Active Sites" },
        { k: "12min", v: "Alarm Response" },
        { k: "24/7", v: "Command Centre" },
      ];
  return (
    <section className="border-y border-white/10 bg-black">
      <div className="max-w-[1600px] mx-auto grid grid-cols-2 md:grid-cols-5">
        {stats.map((s, i) => (
          <div
            key={i}
            className="px-6 py-8 md:py-10 border-r border-white/10 last:border-r-0 md:border-b-0 border-b group hover:bg-amber-500 hover:text-black transition-colors duration-500"
          >
            <div className="font-display font-black text-4xl md:text-5xl tracking-tight text-white group-hover:text-black">
              {s.k}
            </div>
            <div className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500 group-hover:text-black/70">
              {s.v}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

const Manifesto = () => {
  const { t, lang } = useLang();
  const chapters = [
    { n: "01", key: "chapter_01" },
    { n: "02", key: "chapter_02" },
    { n: "03", key: "chapter_03" },
    { n: "04", key: "chapter_04" },
  ];
  return (
    <section className="relative bg-black py-24 md:py-40 px-6 md:px-10">
      <div className="max-w-[1600px] mx-auto">
        <FadeIn>
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 flex items-center gap-3 mb-8">
            <span className="w-8 h-px bg-amber-500" />
            {t("home.manifesto_kicker")}
          </div>
        </FadeIn>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-16 md:gap-y-24">
          {chapters.map((c, i) => (
            <FadeIn key={c.n} delay={i * 0.08}>
              <div className="border-t border-white/15 pt-8 group">
                <div className="flex items-baseline justify-between mb-6">
                  <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">
                    {lang === "fr" ? "Chapitre" : "Chapter"} {c.n}
                  </div>
                  <div className="font-display font-black text-6xl md:text-7xl text-white/10 group-hover:text-amber-500 transition-colors duration-500">
                    {c.n}
                  </div>
                </div>
                <h3 className="font-display uppercase font-black text-4xl md:text-5xl tracking-tight leading-[0.9] text-white">
                  {t(`home.${c.key}_title`)}
                </h3>
                <p className="mt-6 text-zinc-400 text-base md:text-lg leading-relaxed max-w-md">
                  {t(`home.${c.key}_body`)}
                </p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
};

const ServicesBento = () => {
  const { t, lang } = useLang();
  const lp = (p) => localizedPath(p, lang);
  // Featured tiles with specific layout
  const featured = [
    services.find((s) => s.slug === "mining-security"),
    services.find((s) => s.slug === "mobile-patrol"),
    services.find((s) => s.slug === "corporate-security"),
    services.find((s) => s.slug === "event-security"),
    services.find((s) => s.slug === "executive-protection"),
    services.find((s) => s.slug === "construction-security"),
  ].filter(Boolean);

  return (
    <section className="bg-black py-24 md:py-40 px-6 md:px-10">
      <div className="max-w-[1600px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <FadeIn>
              <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 flex items-center gap-3 mb-6">
                <span className="w-8 h-px bg-amber-500" />
                {t("home.services_kicker")}
              </div>
            </FadeIn>
            <FadeIn delay={0.05}>
              <h2 className="font-display uppercase font-black text-5xl md:text-7xl tracking-tight leading-[0.9] text-white max-w-3xl">
                {t("home.services_title")}
              </h2>
            </FadeIn>
          </div>
          <FadeIn delay={0.15}>
            <p className="text-zinc-400 max-w-md">{t("home.services_sub")}</p>
          </FadeIn>
        </div>

        <div className="grid grid-cols-6 auto-rows-[minmax(260px,auto)] gap-4">
          {featured.map((s, i) => {
            const spans = [
              "col-span-6 md:col-span-4 row-span-2",
              "col-span-6 md:col-span-2",
              "col-span-6 md:col-span-2",
              "col-span-6 md:col-span-3",
              "col-span-6 md:col-span-3",
              "col-span-6 md:col-span-6",
            ];
            const isHero = i === 0;
            return (
              <FadeIn key={s.slug} delay={i * 0.05} className={spans[i]}>
                <Link
                  to={lp(`/services/${s.slug}`)}
                  data-testid={TESTIDS.services.card(s.slug)}
                  className="group relative block h-full min-h-[260px] overflow-hidden border border-white/10 hover:border-amber-500 transition-colors duration-500 bg-zinc-950"
                >
                  <img
                    src={s.image}
                    alt={s.name_en}
                    className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-60 transition-opacity duration-700 group-hover:scale-105 transform will-change-transform"
                    style={{ transitionProperty: "opacity, transform" }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />
                  <div className="relative z-10 h-full flex flex-col justify-between p-6 md:p-8">
                    <div className="flex items-start justify-between">
                      <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-amber-500">
                        / {s.number}
                      </div>
                      <ArrowUpRight className="w-5 h-5 text-white/40 group-hover:text-amber-500 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
                    </div>
                    <div>
                      <h3
                        className={`font-display uppercase font-black tracking-tight leading-[0.9] text-white ${
                          isHero ? "text-4xl md:text-6xl" : "text-2xl md:text-3xl"
                        }`}
                      >
                        {lang === "fr" ? s.name_fr : s.name_en}
                      </h3>
                      <p className="mt-3 text-sm text-zinc-400 max-w-md">
                        {isHero ? (lang === "fr" ? s.short_fr : s.short_en) : (lang === "fr" ? s.tagline_fr : s.tagline_en)}
                      </p>
                    </div>
                  </div>
                </Link>
              </FadeIn>
            );
          })}
        </div>

        <div className="mt-10 text-center md:text-left">
          <Link
            to={lp("/services")}
            className="inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em] text-amber-500 hover:text-amber-400 transition-colors"
          >
            {lang === "fr" ? `Voir les ${services.length} services` : `View all ${services.length} services`}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

const CitiesSection = () => {
  const { t, lang } = useLang();
  const lp = (p) => localizedPath(p, lang);
  const preview = cities.slice(0, 16);
  return (
    <section className="bg-black px-6 md:px-10 py-24 md:py-40">
      <div className="max-w-[1600px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-7">
            <FadeIn>
              <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 flex items-center gap-3 mb-6">
                <span className="w-8 h-px bg-amber-500" />
                {t("home.cities_kicker")}
              </div>
            </FadeIn>
            <FadeIn delay={0.05}>
              <h2 className="font-display uppercase font-black text-5xl md:text-7xl tracking-tight leading-[0.9] text-white">
                {t("home.cities_title")}
              </h2>
            </FadeIn>
          </div>
          <FadeIn delay={0.1} className="md:col-span-5 self-end">
            <p className="text-zinc-400 max-w-md">{t("home.cities_sub")}</p>
          </FadeIn>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 border-t border-l border-white/10">
          {preview.map((c, i) => (
            <Link
              key={c.slug}
              to={lp(`/cities/${c.slug}`)}
              data-testid={TESTIDS.cities.card(c.slug)}
              className="group relative aspect-[5/4] border-r border-b border-white/10 p-5 md:p-7 flex flex-col justify-between hover:bg-amber-500 transition-colors duration-500"
            >
              <div className="flex items-start justify-between">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 group-hover:text-black/60">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <ArrowUpRight className="w-4 h-4 text-zinc-600 group-hover:text-black transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </div>
              <div>
                <div className="font-display uppercase font-black text-2xl md:text-3xl tracking-tight text-white group-hover:text-black leading-none">
                  {c.name}
                </div>
                <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 group-hover:text-black/60">
                  {c.region}
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-10">
          <Link
            to="/cities"
            className="inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em] text-amber-500 hover:text-amber-400 transition-colors"
          >
            {lang === "fr" ? `Toutes les ${cities.length}+ villes du Québec` : `All ${cities.length}+ Quebec cities`}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

const CaseStudies = () => {
  const { t, lang } = useLang();
  const items = caseStudies.map((cs, i) => ({
    slug: cs.slug,
    title: cs.title.toUpperCase(),
    caption: cs.tagline,
    image: cs.hero_image,
  }));
  return (
    <section className="bg-black px-6 md:px-10 py-24 md:py-40">
      <div className="max-w-[1600px] mx-auto">
        <FadeIn>
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-amber-500" />
            {t("home.case_kicker")}
          </div>
        </FadeIn>
        <FadeIn delay={0.05}>
          <h2 className="font-display uppercase font-black text-5xl md:text-7xl tracking-tight leading-[0.9] text-white max-w-4xl mb-16">
            {t("home.case_title")}
          </h2>
        </FadeIn>

        <div className="grid grid-cols-12 gap-4 md:gap-6">
          {items.map((it, i) => {
            const layout = [
              "col-span-12 md:col-span-8 aspect-[16/10]",
              "col-span-12 md:col-span-4 aspect-[4/5]",
              "col-span-12 md:col-span-6 aspect-[5/4] md:mt-16",
            ];
            return (
              <FadeIn key={i} delay={i * 0.1} className={layout[i]}>
                <Link
                  to={`/case-studies/${it.slug}`}
                  data-testid={`home-case-${it.slug}`}
                  className="group relative w-full h-full overflow-hidden block"
                >
                  <img
                    src={it.image}
                    alt={it.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    style={{ transitionProperty: "transform" }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                  <div className="absolute top-3 left-3 w-6 h-6 border-t border-l border-amber-500" />
                  <div className="absolute top-3 right-3 w-6 h-6 border-t border-r border-amber-500" />
                  <div className="absolute bottom-3 left-3 w-6 h-6 border-b border-l border-amber-500" />
                  <div className="absolute bottom-3 right-3 w-6 h-6 border-b border-r border-amber-500" />
                  <div className="absolute bottom-6 left-6 right-6 z-10">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-2">
                      ◉ {lang === "fr" ? "Dossier" : "Case File"} · {String(i + 1).padStart(2, "0")}
                    </div>
                    <h3 className="font-display uppercase font-black text-2xl md:text-3xl tracking-tight text-white leading-tight group-hover:text-amber-500 transition-colors">
                      {it.title}
                    </h3>
                    <p className="text-sm text-zinc-300 mt-2 max-w-md">{it.caption}</p>
                    <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      {lang === "fr" ? "Lire le dossier" : "Read case file"} <ArrowRight className="w-3 h-3" />
                    </div>
                  </div>
                </Link>
              </FadeIn>
            );
          })}
        </div>

        <div className="mt-12">
          <Link to="/case-studies" className="inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em] text-amber-500 hover:text-amber-400 transition-colors">
            {lang === "fr" ? "Toutes les études de cas" : "All case studies"}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

const CTA = () => {
  const { t, lang } = useLang();
  return (
    <section className="relative bg-amber-500 text-black overflow-hidden">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-40 relative z-10">
        <FadeIn>
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] mb-8">
            → {t("home.cta_title")}
          </div>
        </FadeIn>
        <FadeIn delay={0.05}>
          <h2 className="font-display uppercase font-black text-[13vw] md:text-[9vw] tracking-tight leading-[0.85]">
            {lang === "fr" ? (<>Déployez<br />avec nous.</>) : (<>Deploy<br />with us.</>)}
          </h2>
        </FadeIn>
        <FadeIn delay={0.1}>
          <div className="mt-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <p className="max-w-md text-lg">{t("home.cta_sub")}</p>
            <Link
              to="/contact"
              className="group inline-flex items-center gap-4 bg-black text-amber-500 h-14 px-6 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-zinc-900 transition-colors"
            >
              {t("common.request_quote")}
              <ArrowUpRight className="w-5 h-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </div>
        </FadeIn>
      </div>
    </section>
  );
};

export default function Home() {
  const { t, lang } = useLang();
  const { pathname } = useLocation();
  const enPath = enTwinFor(pathname);
  const canonicalPath = isFrPath(pathname) ? pathname : enPath;
  const marqueeItems =
    lang === "fr"
      ? ["Licencié BSP", "Bilingue FR/EN", "24/7", "Depuis 2011", "Partout au Québec", "Réponse < 12 min", "Conforme CNESST"]
      : ["BSP Licensed", "Bilingual FR/EN", "24/7 Command", "Since 2011", "Quebec-wide", "Response < 12 min", "CNESST Compliant"];

  return (
    <div>
      <Helmet>
        <title>{t("meta.home_title")}</title>
        <meta name="description" content={t("meta.home_desc")} />
        <meta name="keywords" content={t("meta.home_keywords")} />
        <link rel="canonical" href={`${SITE_BASE_URL}${canonicalPath}`} />
        {(hreflangLinks(enPath) || []).map((l) => (
          <link key={l.hrefLang} rel={l.rel} hrefLang={l.hrefLang} href={l.href} />
        ))}
      </Helmet>
      <JsonLd data={organizationSchema()} />
      <Hero />
      <StatBar />
      <Manifesto />
      <EditorialMarquee items={marqueeItems} />
      <ServicesBento />
      <CitiesSection />
      <CaseStudies />
      <CTA />
    </div>
  );
}
